import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn, formatCurrency, formatPercent } from '../../lib/utils';
import { BarChart3, TrendingUp, DollarSign, Target, Clock, Download, RefreshCw, Calendar } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart, ComposedChart } from 'recharts';
import { useAppMode } from '../../contexts/AppModeContext';

const REVENUE_TREND = [
  { month: 'Aug', charges: 245000, collections: 198000, adjustments: 32000 },
  { month: 'Sep', charges: 258000, collections: 212000, adjustments: 35000 },
  { month: 'Oct', charges: 252000, collections: 205000, adjustments: 33000 },
  { month: 'Nov', charges: 275000, collections: 228000, adjustments: 38000 },
  { month: 'Dec', charges: 268000, collections: 218000, adjustments: 36000 },
  { month: 'Jan', charges: 285000, collections: 235000, adjustments: 40000 },
];

const PAYER_MIX = [
  { name: 'Blue Cross', value: 32 },
  { name: 'Medicare', value: 28 },
  { name: 'UnitedHealth', value: 18 },
  { name: 'Aetna', value: 12 },
  { name: 'Self-Pay', value: 10 },
];

const SERVICE_MIX = [
  { service: 'E/M Visits', revenue: 145000, claims: 892 },
  { service: 'Procedures', revenue: 85000, claims: 245 },
  { service: 'Labs', revenue: 32000, claims: 1245 },
  { service: 'Imaging', revenue: 28000, claims: 156 },
  { service: 'Other', revenue: 18000, claims: 198 },
];

const DENIAL_TREND = [
  { month: 'Aug', rate: 4.2, amount: 12500 },
  { month: 'Sep', rate: 3.8, amount: 11200 },
  { month: 'Oct', rate: 4.5, amount: 13800 },
  { month: 'Nov', rate: 3.2, amount: 9500 },
  { month: 'Dec', rate: 3.5, amount: 10200 },
  { month: 'Jan', rate: 3.1, amount: 8900 },
];

const PROVIDER_PRODUCTIVITY = [
  { provider: 'Dr. Wilson', visits: 245, rvu: 892, collections: 125000 },
  { provider: 'Dr. Chen', visits: 218, rvu: 756, collections: 98000 },
  { provider: 'Dr. Roberts', visits: 198, rvu: 685, collections: 92000 },
  { provider: 'Dr. Thompson', visits: 176, rvu: 612, collections: 78000 },
  { provider: 'Dr. Park', visits: 165, rvu: 548, collections: 72000 },
];

const COLORS = ['#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db'];

const RevQAnalytics = () => {
  const { isDemo } = useAppMode();
  const [dateRange, setDateRange] = useState('6m');

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Analytics & Insights</h1>
          <p className="text-slate-500 mt-1">Comprehensive revenue cycle performance metrics</p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Last Month</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Collections</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(1296000)}</p>
                <p className="text-xs text-emerald-600">+8.2% vs prior</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collection Rate</p>
                <p className="text-xl font-bold">87.4%</p>
                <p className="text-xs text-emerald-600">+2.1% vs prior</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Days in A/R</p>
                <p className="text-xl font-bold">26 days</p>
                <p className="text-xs text-emerald-600">-3 days vs prior</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Denial Rate</p>
                <p className="text-xl font-bold">3.1%</p>
                <p className="text-xs text-emerald-600">-0.8% vs prior</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={REVENUE_TREND}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="charges" name="Charges" fill="#e2e8f0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collections" name="Collections" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="adjustments" name="Adjustments" stroke="#f59e0b" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payer Mix */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Payer Mix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={PAYER_MIX} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}%`}>
                    {PAYER_MIX.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Denial Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Denial Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={DENIAL_TREND}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="rate" name="Denial Rate %" stroke="#ef4444" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="amount" name="$ Denied" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Mix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Revenue by Service Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={SERVICE_MIX} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis dataKey="service" type="category" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="revenue" name="Revenue" fill="#000" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Provider Productivity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Provider Productivity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Provider</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Visits</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">RVUs</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Collections</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">$/Visit</th>
                </tr>
              </thead>
              <tbody>
                {PROVIDER_PRODUCTIVITY.map((p, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">{p.provider}</td>
                    <td className="py-3 px-4 text-right">{p.visits}</td>
                    <td className="py-3 px-4 text-right">{p.rvu}</td>
                    <td className="py-3 px-4 text-right text-emerald-600 font-medium">{formatCurrency(p.collections)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(p.collections / p.visits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-600">Clean Claim Rate</p>
            <p className="text-2xl font-bold">97.8%</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-600">First Pass Rate</p>
            <p className="text-2xl font-bold">94.2%</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-600">A/R > 90 Days</p>
            <p className="text-2xl font-bold">8.5%</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-600">Avg Reimbursement</p>
            <p className="text-2xl font-bold">$142</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RevQAnalytics;
